/**
 * Till pricing: the POS till's order calculation, ported to the storefront.
 *
 * Owner ruling (2026-10-10): the till's price is authoritative. The OOv2
 * checkout estimate must equal the till's amounts field by field, to the
 * piaster. This module is a faithful port of pos_v2
 * `OrderCalculatorService.calculate` (lib/core/utils/order_calculator_service.dart)
 * as driven by `OrderPricingRun.price` for a fresh online order:
 *
 *   - lines priced on the menu entry: base + variant adjustment + modifier
 *     price per unit, times quantity; item taxes are the menu entry's own
 *     `taxes[]` (none on an exempt menu);
 *   - promotion eligibility: status, validity window (dates, weekday, time of
 *     day at minute granularity, end minute inclusive), min order on the
 *     chargeable gross, branch / channel / order type (pickup == takeaway) /
 *     segments (fail closed), required items (matchScope), autoApply or picked
 *     (an entered code = a picked promotion), geofence;
 *   - priority desc; first applied wins, a non-stackable winner stops the
 *     rest, later non-stackables are skipped, stackables add;
 *   - line-level rewards through the shared reward engine (`evaluateReward`);
 *     free items auto-added and re-priced in a second pass;
 *   - order-level rewards re-evaluated in sequence on the remaining net;
 *   - PASS 2: proration, per-line rounding with sub-cent carry, inclusive VAT
 *     extracted at the combined rate, exclusive VAT on the 2dp net base;
 *   - service charge (dine-in only) on the goods net of included VAT after
 *     every reduction and the loyalty redemption, plus its own VAT;
 *   - delivery fee and its VAT; surcharges; loyalty redemption after tax,
 *     capped at the bill; half-up 2dp rounding.
 *
 * Pure: no React, no network, no clock (pass `now`).
 */
import type { Promotion } from "@/api/online-ordering-service/model/promotion";
import type { UiMenuItem } from "@/hooks/useMenu";
import {
  evaluateReward,
  UnknownRewardError,
  type CalcContext,
  type CalcLine,
  type LoyaltySignals,
  type Reward,
  type RewardCondition,
} from "@/lib/promotion-engine";
import { isPromotionWithinWindow } from "@/lib/promotion-window";
import {
  computeAppliedSurcharges,
  type AppliedSurcharge,
  type SurchargeDefinition,
  type TaxRef,
} from "@/lib/surcharges";

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Half-up to cents, the till's `roundMoney` (with its 1e-9 nudge). */
export function roundMoney(value: number): number {
  const scaled = value >= 0 ? value * 100 + 1e-9 : value * 100 - 1e-9;
  // Dart's round() is half away from zero.
  const r = scaled >= 0 ? Math.floor(scaled + 0.5) : -Math.floor(-scaled + 0.5);
  const out = r / 100;
  return out === 0 ? 0 : out;
}
const r2 = roundMoney;
const EPS = 1e-9;

export type TillTax = {
  id?: string | null;
  name?: any;
  rate: number;
  isIncludedInPrice?: boolean | null;
  scope?: string | null;
  isActive?: boolean | null;
  isDeleted?: boolean | null;
};

/** A cart line as the storefront holds it (structurally a `CartLine`). */
export type TillCartLine = {
  id?: string;
  itemId: string;
  qty: number;
  /** Fallbacks for an item no longer on the menu. */
  price?: number;
  basePrice?: number;
  categoryId?: string | null;
  selectedVariants?: ReadonlyArray<{ variantOptionId: string; priceAdjustment?: number | null }> | null;
  selectedModifiers?: ReadonlyArray<{ modifierOptionId: string; quantity?: number | null; unitPrice?: number | null }> | null;
};

export type TillOrderType = string; // "pickup" | "takeaway" | "dineIn" | "delivery" (any casing / dine_in)

export type PriceCartInput = {
  cart: ReadonlyArray<TillCartLine>;
  menuItems: ReadonlyArray<UiMenuItem>;
  promotions?: ReadonlyArray<Promotion>;
  /** Code typed by the guest; a promotion whose promoCode matches (trimmed, case-insensitive) is picked. */
  enteredCode?: string | null;
  /** Every code typed by the guest; each picks its promotion, stacking rules decide what combines. */
  enteredCodes?: ReadonlyArray<string> | null;
  orderType: TillOrderType;
  /** Order source; the storefront is always "online". */
  channel?: string;
  branchId: string;
  /** Wall-clock time in branch time. */
  now: Date;
  /** The menu's tax policy is exempt: no item taxes. */
  menuTaxExempt?: boolean;
  /** Dine-in service charge percent (ignored for other order types). */
  serviceChargePercent?: number;
  /** `service_fee`-scoped taxes. */
  serviceFeeTaxes?: ReadonlyArray<TillTax> | null;
  /** Delivery fee (only charged on delivery orders). */
  deliveryFee?: number;
  /** `delivery_fee`-scoped taxes. */
  deliveryTaxes?: ReadonlyArray<TillTax> | null;
  /** `order_total`-scoped taxes. */
  orderTotalTaxes?: ReadonlyArray<TillTax> | null;
  /** Requested loyalty redemption value (money); capped at the bill. */
  loyaltyRedemption?: number;
  /** Delivery location, for geofenced promotions (fail closed without it). */
  deliveryCoordinates?: { lat: number; lng: number } | null;
  surcharges?: ReadonlyArray<SurchargeDefinition> | null;
  surchargeTaxesById?: Record<string, TaxRef>;
};

export type TillLineTax = {
  taxId: string | null;
  rate: number;
  isIncludedInPrice: boolean;
  taxableAmount: number;
  calculatedAmount: number;
};

export type TillPricedLine = {
  /** Index into `input.cart`, or null for a promotion-granted line. */
  cartIndex: number | null;
  cartLineId: string | null;
  itemId: string;
  quantity: number;
  categoryId: string | null;
  grantedByPromotionId: string | null;
  variantOptionIds: string[];
  unitPrice: number;
  baseAmount: number;
  variantAmount: number;
  modifierAmount: number;
  subtotalAmount: number;
  discountAmount: number;
  promotionAmount: number;
  orderPromotionAmount: number;
  orderDiscountAmount: number;
  lineTotalBeforeTax: number;
  taxAmount: number;
  taxIncluded: number;
  taxExcluded: number;
  totalAmount: number;
  appliedPromotionIds: string[];
  taxInfo: TillLineTax[];
};

export type TillFreeItemLine = {
  promotionId: string;
  itemId: string;
  variantId: string | null;
  quantity: number;
  unitPrice: number;
};

export type PriceCartResult = {
  itemsSubtotal: number;
  promotionAmount: number;
  discountAmount: number;
  taxAmount: number;
  taxIncluded: number;
  taxExclusive: number;
  serviceFeeAmount: number;
  deliveryFeeAmount: number;
  surchargeAmount: number;
  loyaltyDiscountAmount: number;
  /** Total before the loyalty redemption (the bill the redemption is capped at). */
  totalBeforeLoyalty: number;
  totalAmount: number;
  appliedPromotionIds: string[];
  promotionAmounts: Array<{ promotionId: string; amount: number }>;
  loyaltySignals: LoyaltySignals;
  freeDelivery: boolean;
  /** Promotions whose reward kind could not be computed (server must reconcile). */
  reconcilePromotionIds: string[];
  lines: TillPricedLine[];
  freeItemLines: TillFreeItemLine[];
  appliedSurcharges: AppliedSurcharge[];
};

/* ───────────────────────── reward mapping ───────────────────────── */

const UNIFIED_KEYS = [
  "discountType", "value", "cap", "target", "targetItemIds", "itemId", "quantity", "variantId",
  "sourceCondition", "multiplier", "amount", "addOnItemId", "addOnPrice", "itemRequirements", "groups",
  "bundlePrice", "maxUnits", "unitSelection", "maxApplications", "winProbability", "then",
] as const;

/**
 * The till's `_rewardFor`: the unified reward (`kind` + contract fields, passed
 * through by the BFF) when present, else the legacy flat reward bridged the
 * same way the till's `_legacyRewardBridge` does. Null = nothing to apply.
 */
export function rewardForPromotion(promo: Promotion): Reward | null {
  const raw = (promo as any)?.reward as Record<string, any> | null | undefined;
  if (!raw) return null;
  if (typeof raw.kind === "string" && raw.kind) {
    const out: Record<string, any> = { kind: raw.kind };
    for (const k of UNIFIED_KEYS) if (raw[k] !== undefined && raw[k] !== null) out[k] = raw[k];
    if (out.value === undefined && raw.discountValue != null) out.value = raw.discountValue;
    if (out.cap === undefined && raw.discountCap != null) out.cap = raw.discountCap;
    if (out.cap != null && !(out.cap > 0)) out.cap = null;
    if (out.discountType != null) out.discountType = out.discountType === "percentage" ? "percentage" : "fixed";
    if (out.kind === "free_item" && out.quantity == null) out.quantity = 1;
    return out as Reward;
  }
  const type = raw.type;
  const discountType = raw.discountType === "percentage" ? "percentage" : "fixed";
  const cap = raw.discountCap != null && raw.discountCap > 0 ? raw.discountCap : null;
  switch (type) {
    case "order_discount":
      return { kind: "order_discount", discountType, value: Number(raw.discountValue ?? 0), cap };
    case "item_discount":
      return {
        kind: "item_discount",
        discountType,
        value: Number(raw.discountValue ?? 0),
        cap,
        target: raw.discountTarget === "specific_items" ? "specific_items" : "matching_items",
        targetItemIds: raw.discountTargetItemIds ?? null,
        ...(raw.maxUnits != null ? { maxUnits: raw.maxUnits } : {}),
        ...(raw.unitSelection != null ? { unitSelection: raw.unitSelection } : {}),
      };
    case "free_item": {
      const freeId =
        raw.freeItemCriteria?.freeItemId ??
        (Array.isArray(raw.discountTargetItemIds) && raw.discountTargetItemIds.length
          ? raw.discountTargetItemIds[0]
          : null);
      return {
        kind: "free_item",
        itemId: freeId,
        quantity: raw.freeItemCriteria?.quantity ?? 1,
        sourceCondition: raw.freeItemCriteria?.sourceCondition ?? "specific_item",
      };
    }
    case "free_delivery":
      return { kind: "free_delivery" };
    default:
      // Contract-shaped reward under the legacy `type` discriminator.
      if (typeof type === "string" && type) return rewardForPromotion({ ...promo, reward: { ...raw, kind: type } } as any);
      return null;
  }
}

/* ───────────────────────── eligibility ───────────────────────── */

const norm = (s: unknown) => String(s ?? "").replace(/[_\s-]/g, "").toLowerCase();
const ORDER_TYPE_ALIASES: Record<string, string[]> = {
  pickup: ["pickup", "takeaway"],
  takeaway: ["pickup", "takeaway"],
};

/** The till's `_meetsTargeting`: empty lists mean all; segments fail closed. */
export function meetsTargeting(
  criteria: any,
  ctx: { branchId: string; channel: string; orderType: string },
): boolean {
  if (!criteria) return true;
  const branches: string[] | null | undefined = criteria.applicableBranchIds;
  if (branches && branches.length && !branches.includes(ctx.branchId)) return false;
  const sources: string[] | null | undefined = criteria.applicableOrderSources;
  if (sources && sources.length && !sources.some((s) => norm(s) === norm(ctx.channel))) return false;
  const types: string[] | null | undefined = criteria.applicableOrderTypes;
  if (types && types.length) {
    const ot = norm(ctx.orderType);
    const accepted = ORDER_TYPE_ALIASES[ot] ?? [ot];
    if (!types.some((t) => accepted.includes(norm(t)))) return false;
  }
  const segments: string[] | null | undefined = criteria.applicableCustomerSegmentIds;
  if (segments && segments.length) return false;
  return true;
}

type WorkLine = {
  index: number;
  key: string;
  cartIndex: number | null;
  cartLineId: string | null;
  itemId: string;
  categoryId: string | null;
  quantity: number;
  unitPrice: number;
  variantAmount: number;
  modifierAmount: number;
  subtotalAmount: number;
  discountAmount: number;
  promotionAmount: number;
  appliedPromotionIds: string[];
  grantedByPromotionId: string | null;
  variantOptionIds: string[];
  taxes: TillTax[];
};

/** The till's `_meetsRequiredItemCriteria` (matchScope aware; granted lines never count). */
export function meetsRequiredItemCriteria(
  criteria: ReadonlyArray<any> | null | undefined,
  lines: ReadonlyArray<{ itemId: string; categoryId: string | null; quantity: number; grantedByPromotionId: string | null }>,
): boolean {
  if (!criteria || criteria.length === 0) return true;
  for (const req of criteria) {
    const hasItemIds = !!req.itemIds && req.itemIds.length > 0;
    const hasCatIds = !!req.categoryIds && req.categoryIds.length > 0;
    let total = 0;
    const byItem = new Map<string, number>();
    for (const l of lines) {
      if (l.quantity <= 0 || l.grantedByPromotionId != null) continue;
      const matches = hasItemIds
        ? req.itemIds.includes(l.itemId)
        : hasCatIds
        ? l.categoryId != null && req.categoryIds.includes(l.categoryId)
        : true;
      if (matches) {
        total += l.quantity;
        byItem.set(l.itemId, (byItem.get(l.itemId) ?? 0) + l.quantity);
      }
    }
    const min = Number(req.minQuantity ?? 0);
    if (norm(req.matchScope) === "eachmatchingitem") {
      if (byItem.size === 0) return false;
      for (const q of byItem.values()) if (q < min) return false;
    } else if (total < min) {
      return false;
    }
  }
  return true;
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const taxInForce = (t: TillTax) => t.isActive !== false && t.isDeleted !== true;

/* ───────────────────────── menu resolution ───────────────────────── */

type MenuEntry = { item: UiMenuItem; raw: any };

function menuIndex(items: ReadonlyArray<UiMenuItem>): Map<string, MenuEntry> {
  const m = new Map<string, MenuEntry>();
  for (const it of items) if (!m.has(it.id)) m.set(it.id, { item: it, raw: (it as any).raw ?? {} });
  return m;
}

function itemTaxes(entry: MenuEntry | undefined, exempt: boolean): TillTax[] {
  if (exempt || !entry) return [];
  const taxes = entry.raw?.taxes;
  return Array.isArray(taxes) ? taxes.filter(Boolean) : [];
}

function findVariantOption(raw: any, id: string): any {
  for (const g of raw?.variantGroups ?? []) for (const o of g?.options ?? []) if (o?.id === id) return o;
  return null;
}
function findModifierOption(raw: any, id: string): any {
  for (const g of raw?.modifierGroups ?? []) for (const o of g?.options ?? []) if (o?.id === id) return o;
  return null;
}

/* ───────────────────────── the calculation ───────────────────────── */

type PendingGrant = { promotionId: string; itemId: string; variantId: string | null; quantity: number };

type CoreResult = {
  result: PriceCartResult;
  pendingGrants: PendingGrant[];
  zeroOrderPromotionIds: Set<string>;
};

function buildLines(input: PriceCartInput, idx: Map<string, MenuEntry>): WorkLine[] {
  const exempt = input.menuTaxExempt === true;
  return input.cart.map((c, i) => {
    const entry = idx.get(c.itemId);
    const raw = entry?.raw;
    const variantIds = (c.selectedVariants ?? []).map((v) => v.variantOptionId);
    let unitPrice: number;
    let variantAmount = 0;
    let modifierAmount = 0;
    if (entry) {
      unitPrice = Number(raw?.price?.amount ?? entry.item.price ?? 0);
      for (const v of c.selectedVariants ?? []) {
        const opt = findVariantOption(raw, v.variantOptionId);
        variantAmount += Number(opt ? opt.price?.amount ?? 0 : v.priceAdjustment ?? 0);
      }
      for (const m of c.selectedModifiers ?? []) {
        const opt = findModifierOption(raw, m.modifierOptionId);
        const price = Number(opt ? opt.price?.amount ?? 0 : m.unitPrice ?? 0);
        modifierAmount += price * Number(m.quantity ?? 1);
      }
    } else {
      // Not on the loaded menu: price it as the cart line was added.
      for (const v of c.selectedVariants ?? []) variantAmount += Number(v.priceAdjustment ?? 0);
      for (const m of c.selectedModifiers ?? []) modifierAmount += Number(m.unitPrice ?? 0) * Number(m.quantity ?? 1);
      unitPrice = c.basePrice ?? Math.max(0, (c.price ?? 0) - variantAmount - modifierAmount);
    }
    const quantity = Math.max(0, Math.trunc(Number(c.qty) || 0));
    return {
      index: i,
      key: `#${i}`,
      cartIndex: i,
      cartLineId: c.id ?? null,
      itemId: c.itemId,
      categoryId: c.categoryId ?? (entry ? (entry.item as any).cat ?? null : null),
      quantity,
      unitPrice,
      variantAmount,
      modifierAmount,
      subtotalAmount: (unitPrice + variantAmount + modifierAmount) * quantity,
      discountAmount: 0,
      promotionAmount: 0,
      appliedPromotionIds: [],
      grantedByPromotionId: null,
      variantOptionIds: variantIds,
      taxes: itemTaxes(entry, exempt),
    };
  });
}

const lineRoom = (l: WorkLine) => Math.max(0, l.subtotalAmount - l.discountAmount - l.promotionAmount);

function applyLinePromotion(lines: WorkLine[], key: string, amount: number, promoId: string | null): boolean {
  const l = lines.find((x) => x.key === key);
  if (!l) return false;
  let calc = amount;
  const room = lineRoom(l);
  if (calc > room) calc = room;
  if (calc <= 0) return false;
  l.promotionAmount += calc;
  if (promoId) l.appliedPromotionIds.push(promoId);
  return true;
}

function distributeLinePromotion(
  lines: WorkLine[],
  amount: number,
  matchedItemIds: Set<string> | null,
  promoId: string | null,
): boolean {
  const matched: WorkLine[] = [];
  let totalHeadroom = 0;
  for (const l of lines) {
    if (matchedItemIds && !matchedItemIds.has(l.itemId)) continue;
    const h = lineRoom(l);
    if (h <= 0) continue;
    matched.push(l);
    totalHeadroom += h;
  }
  if (!matched.length || totalHeadroom <= 0) return false;
  const applicable = amount > totalHeadroom ? totalHeadroom : amount;
  let applied = false;
  let distributed = 0;
  for (let i = 0; i < matched.length; i++) {
    const l = matched[i];
    const headroom = lineRoom(l);
    let share: number;
    if (i === matched.length - 1) share = applicable - distributed;
    else {
      share = applicable * (headroom / totalHeadroom);
      distributed += share;
    }
    if (share > headroom) share = headroom;
    if (share <= 0) continue;
    l.promotionAmount += share;
    if (promoId) l.appliedPromotionIds.push(promoId);
    applied = true;
  }
  return applied;
}

function applyFreeItemDiscount(
  lines: WorkLine[],
  outcome: ReturnType<typeof evaluateReward>,
  reward: Extract<Reward, { kind: "free_item" }>,
  promoId: string | null,
): boolean {
  if (outcome.discountAmount <= 0 || outcome.appliedToLineId == null) return false;
  if (outcome.appliedToLineId !== "DISTRIBUTE") {
    return applyLinePromotion(lines, outcome.appliedToLineId, outcome.discountAmount, promoId);
  }
  const allocs = outcome.lineAllocations ?? {};
  if (Object.keys(allocs).length) {
    let applied = false;
    for (const [k, v] of Object.entries(allocs)) if (applyLinePromotion(lines, k, v, promoId)) applied = true;
    return applied;
  }
  const matched = lines
    .filter(
      (l) =>
        l.itemId === reward.itemId &&
        (reward.variantId == null || l.variantOptionIds.includes(reward.variantId)),
    )
    .sort((a, b) => {
      const va = a.quantity > 0 ? a.subtotalAmount / a.quantity : 0;
      const vb = b.quantity > 0 ? b.subtotalAmount / b.quantity : 0;
      return va - vb || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
    });
  let remaining = outcome.discountAmount;
  let applied = false;
  for (const l of matched) {
    if (remaining <= 0) break;
    const headroom = lineRoom(l);
    if (headroom <= 0) continue;
    const credit = remaining < headroom ? remaining : headroom;
    l.promotionAmount += credit;
    remaining -= credit;
    if (promoId) l.appliedPromotionIds.push(promoId);
    applied = true;
  }
  return applied;
}

function calcCore(
  input: PriceCartInput,
  lines: WorkLine[],
  excluded: Set<string>,
  collectPendingGrants: boolean,
): CoreResult {
  const channel = input.channel ?? "online";
  const orderType = input.orderType;
  const isDineIn = norm(orderType) === "dinein";
  const isDelivery = norm(orderType) === "delivery";
  const now = input.now;
  const pendingGrants: PendingGrant[] = [];

  // PASS 0
  let itemsGrossSubtotal = 0;
  let chargeableGrossSubtotal = 0;
  for (const l of lines) {
    itemsGrossSubtotal += l.subtotalAmount;
    if (l.grantedByPromotionId == null) chargeableGrossSubtotal += l.subtotalAmount;
  }
  // PASS 1: no manual line discounts online.
  const itemsTotalLineDiscount = 0;

  const inBase = (l: WorkLine) => l.grantedByPromotionId == null;
  const orderReductionBaseNow = () => lines.filter(inBase).reduce((s, l) => s + lineRoom(l), 0);

  // Picked promotions: an entered code matches promoCode (trimmed, case-insensitive).
  const codes = new Set(
    [input.enteredCode, ...(input.enteredCodes ?? [])]
      .map((c) => (c ?? "").trim().toLowerCase())
      .filter((c) => c),
  );
  const picked = new Set<string>();
  if (codes.size) {
    for (const p of input.promotions ?? []) {
      if (p.id && codes.has((p.promoCode ?? "").trim().toLowerCase())) picked.add(p.id);
    }
  }

  type Eligible = { promo: Promotion };
  const eligible: Eligible[] = [];
  const seen = new Set<string>();
  for (const promo of input.promotions ?? []) {
    if (promo.id) {
      if (seen.has(promo.id)) continue; // the same promotion listed twice
      seen.add(promo.id);
    }
    if (promo.isDeleted === true) continue;
    if (promo.status != null && norm(promo.status) !== "active") continue;
    if (promo.id && excluded.has(promo.id)) continue;
    if (!isPromotionWithinWindow(promo.validity, now)) continue;
    const criteria: any = promo.criteria ?? {};
    if (criteria.minOrderValue != null && chargeableGrossSubtotal < criteria.minOrderValue) continue;
    if (!meetsTargeting(criteria, { branchId: input.branchId, channel, orderType })) continue;
    if (!meetsRequiredItemCriteria(criteria.requiredItemCriteria, lines)) continue;
    const isPicked = !!promo.id && picked.has(promo.id);
    if (promo.autoApply !== true && !isPicked) continue;
    const geofence = criteria.geofence;
    if (geofence) {
      const c = input.deliveryCoordinates;
      if (!c) continue;
      if (haversineMeters(Number(geofence.lat), Number(geofence.lng), c.lat, c.lng) > Number(geofence.radiusM)) continue;
    }
    eligible.push({ promo });
  }
  // Priority desc (stable, like Dart's insertion sort for short lists).
  eligible.sort((a, b) => (b.promo.priority ?? 0) - (a.promo.priority ?? 0));

  let orderPromotionAmount = 0;
  const orderAppliedPromotionIds: string[] = [];
  const promoOrderPortion = new Map<string, number>();
  const promoLinePortion = new Map<string, number>();
  const sumLinePromotions = () => lines.reduce((s, l) => s + l.promotionAmount, 0);
  let isFreeDeliveryGranted = false;
  const appliedOrderRewards: Array<{ id: string | null; reward: Reward }> = [];

  const rewardCtx: CalcContext = {
    branchId: input.branchId,
    orderLines: lines.map(
      (l): CalcLine => ({
        lineId: l.key,
        itemId: l.itemId,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        lineSubtotalAmount: lineRoom(l),
        variantOptionIds: l.variantOptionIds,
        categoryId: l.categoryId,
        grantedByPromotionId: l.grantedByPromotionId,
      }),
    ),
    itemsSubtotalAmount: itemsGrossSubtotal,
    orderSubtotalAmount: itemsGrossSubtotal,
  };

  const reconcile: string[] = [];
  const signals: LoyaltySignals = {};
  let anyPromoAppliedSoFar = false;
  const claimedUnits: Record<string, number> = {};

  for (const { promo } of eligible) {
    if (promo.stackable !== true && anyPromoAppliedSoFar) continue;
    let promoApplied = false;
    const lineBefore = sumLinePromotions();
    const orderBefore = orderPromotionAmount;
    const reward = rewardForPromotion(promo);
    const promoId = promo.id ?? null;
    if (!reward) {
      if (promoId) reconcile.push(promoId);
      continue;
    }
    const condition: RewardCondition = {
      requirement: { requiredItemCriteria: (promo.criteria as any)?.requiredItemCriteria ?? null },
    };
    let outcome: ReturnType<typeof evaluateReward>;
    try {
      outcome = evaluateReward(
        reward,
        reward.kind === "order_discount" ? { ...rewardCtx, orderSubtotalAmount: orderReductionBaseNow() } : rewardCtx,
        promoId,
        condition,
        claimedUnits,
      );
    } catch (e) {
      if (e instanceof UnknownRewardError) {
        if (promoId) reconcile.push(promoId);
        continue;
      }
      throw e;
    }

    if (outcome.freeDelivery) {
      isFreeDeliveryGranted = true;
      promoApplied = true;
    }
    if (outcome.discountAmount > 0 && reward.kind !== "free_item") {
      const allocs = outcome.lineAllocations ?? {};
      if (reward.kind === "order_discount") {
        orderPromotionAmount += outcome.discountAmount;
        appliedOrderRewards.push({ id: promoId, reward });
        promoApplied = true;
      } else if (Object.keys(allocs).length) {
        for (const [k, v] of Object.entries(allocs)) if (applyLinePromotion(lines, k, v, promoId)) promoApplied = true;
      } else if (outcome.appliedToLineId === "DISTRIBUTE" || outcome.appliedToLineId == null) {
        // item_discount / bundle: across the matching (or all) lines. The
        // till's engine reports an order_total item_discount as DISTRIBUTE.
        let matched: Set<string> | null = null;
        if (reward.kind === "item_discount" && reward.target === "specific_items") {
          matched = new Set(reward.targetItemIds ?? []);
        } else if (reward.kind === "bundle") {
          matched = new Set(reward.itemRequirements.map((r) => r.itemId));
        }
        if (distributeLinePromotion(lines, outcome.discountAmount, matched, promoId)) promoApplied = true;
      } else if (applyLinePromotion(lines, outcome.appliedToLineId, outcome.discountAmount, promoId)) {
        promoApplied = true;
      }
    }
    if (reward.kind === "free_item" && outcome.discountAmount > 0) {
      if (applyFreeItemDiscount(lines, outcome, reward, promoId)) promoApplied = true;
    }
    if (collectPendingGrants && reward.kind === "free_item" && outcome.freeItemsToAdd?.length && promoId) {
      for (const g of outcome.freeItemsToAdd) {
        pendingGrants.push({ promotionId: promoId, itemId: g.itemId, variantId: g.variantId ?? null, quantity: g.quantity });
      }
    }
    for (const [k, v] of Object.entries(outcome.loyaltySignals ?? {})) {
      if (v == null) continue;
      if (k === "earnMultiplier") {
        const cur = signals.earnMultiplier ?? 1.0;
        signals.earnMultiplier = cur >= v ? cur : v;
      } else {
        (signals as any)[k] = ((signals as any)[k] ?? 0) + v;
      }
    }
    if (Object.keys(outcome.loyaltySignals ?? {}).length) promoApplied = true;

    if (promoApplied && promoId) {
      orderAppliedPromotionIds.push(promoId);
      promoOrderPortion.set(promoId, (promoOrderPortion.get(promoId) ?? 0) + (orderPromotionAmount - orderBefore));
      promoLinePortion.set(promoId, (promoLinePortion.get(promoId) ?? 0) + (sumLinePromotions() - lineBefore));
      anyPromoAppliedSoFar = true;
      if (reward.kind === "free_item" || reward.kind === "category_bundle") {
        for (const [lid, amt] of Object.entries(outcome.lineAllocations ?? {})) {
          // Whole units taken from this line (value / unit value).
          const l = lines.find((x) => x.key === lid);
          const unit = l && l.quantity > 0 ? l.subtotalAmount / l.quantity : 0;
          const units = unit > 0 ? Math.round(amt / unit) : 0;
          claimedUnits[lid] = (claimedUnits[lid] ?? 0) + units;
        }
      }
      if (promo.stackable !== true) break;
    }
  }

  // ORDER-LEVEL REDUCTIONS (sequential on the remaining net). No manual
  // order discount online.
  const orderReductionBase = orderReductionBaseNow();
  const orderDiscountAmount = 0;
  const lineRemaining = lines.map((l) => (inBase(l) ? lineRoom(l) : 0));
  const lineOrderPromotion = lines.map(() => 0);
  const lineOrderPromotionIds: string[][] = lines.map(() => []);
  if (appliedOrderRewards.length) {
    orderPromotionAmount = 0;
    for (const { id, reward } of appliedOrderRewards) {
      const scopeRemaining = lineRemaining.reduce((a, w) => a + w, 0);
      let amount =
        scopeRemaining > EPS
          ? evaluateReward(reward, { ...rewardCtx, orderSubtotalAmount: scopeRemaining }).discountAmount
          : 0;
      if (amount > scopeRemaining) amount = scopeRemaining;
      if (amount > 0) {
        const factor = amount / scopeRemaining;
        for (let i = 0; i < lines.length; i++) {
          const share = lineRemaining[i] * factor;
          if (share <= 0) continue;
          lineRemaining[i] -= share;
          lineOrderPromotion[i] += share;
          if (id) lineOrderPromotionIds[i].push(id);
        }
      }
      orderPromotionAmount += amount;
      if (id) promoOrderPortion.set(id, amount);
    }
  }
  if (orderPromotionAmount > orderReductionBase - orderDiscountAmount) {
    const unclamped = orderPromotionAmount;
    orderPromotionAmount = orderReductionBase - orderDiscountAmount;
    const factor = unclamped > 0 ? orderPromotionAmount / unclamped : 0;
    for (const [k, v] of promoOrderPortion) promoOrderPortion.set(k, v * factor);
    for (let i = 0; i < lineOrderPromotion.length; i++) lineOrderPromotion[i] *= factor;
  }
  const zeroOrderPromotionIds = new Set<string>();
  for (const { id } of appliedOrderRewards) {
    if (id && r2(promoOrderPortion.get(id) ?? 0) <= 0 && r2(promoLinePortion.get(id) ?? 0) <= 0) {
      zeroOrderPromotionIds.add(id);
    }
  }
  const appliedIds = orderAppliedPromotionIds.filter((id) => !zeroOrderPromotionIds.has(id));
  // A picked promotion left out because it was worth 0.00 stays selected.
  for (const id of excluded) if (picked.has(id) && !appliedIds.includes(id)) appliedIds.push(id);
  const promotionAmounts = appliedIds
    .map((id) => ({ promotionId: id, amount: r2((promoOrderPortion.get(id) ?? 0) + (promoLinePortion.get(id) ?? 0)) }))
    .filter((p) => p.amount > 0);

  // PASS 2
  const orderTotalTaxes = (input.orderTotalTaxes ?? []).filter(taxInForce);
  const orderTotalTaxIds = new Set(orderTotalTaxes.map((t) => t.id).filter(Boolean) as string[]);
  let itemsInclusiveTax = 0;
  let itemsExclusiveTax = 0;
  let itemsNetOfInclusiveTaxBase = 0;
  let itemsPreDiscountNetBase = 0;
  let accumulatedProratedDiscount = 0;
  let accumulatedProratedPromotion = 0;
  let accumulatedGrossRoundingError = 0;
  const weights = lines.map((l) => (inBase(l) ? lineRoom(l) : 0));
  const totalWeight = weights.reduce((a, w) => a + w, 0);
  let lastProrationIdx = -1;
  weights.forEach((w, i) => {
    if (w > EPS) lastProrationIdx = i;
  });
  let lastPositiveNetIdx = -1;
  lines.forEach((l, i) => {
    if (lineRoom(l) > EPS) lastPositiveNetIdx = i;
  });

  const priced: TillPricedLine[] = [];
  for (let idx = 0; idx < lines.length; idx++) {
    const l = lines[idx];
    const weight = weights[idx];
    const proportion = totalWeight > 0 && weight > 0 ? weight / totalWeight : 0;
    let proratedOrderDiscount = 0;
    let proratedOrderPromotion = 0;
    if (idx === lastProrationIdx) {
      proratedOrderDiscount = orderDiscountAmount - accumulatedProratedDiscount;
      proratedOrderPromotion = orderPromotionAmount - accumulatedProratedPromotion;
    } else if (proportion > 0) {
      proratedOrderDiscount = orderDiscountAmount * proportion;
      proratedOrderPromotion = lineOrderPromotion[idx];
      accumulatedProratedDiscount += proratedOrderDiscount;
      accumulatedProratedPromotion += proratedOrderPromotion;
    }
    const preciseLineGross = Math.max(
      0,
      l.subtotalAmount - l.discountAmount - l.promotionAmount - proratedOrderDiscount - proratedOrderPromotion,
    );
    const isLastLine = lastPositiveNetIdx >= 0 ? idx === lastPositiveNetIdx : idx === lines.length - 1;
    const lineTotalGross = isLastLine
      ? r2(Math.max(0, preciseLineGross + accumulatedGrossRoundingError))
      : r2(preciseLineGross);
    if (!isLastLine) accumulatedGrossRoundingError += preciseLineGross - lineTotalGross;

    const lineTaxes = l.taxes.filter((t) => !(t.id && orderTotalTaxIds.has(t.id)));
    const inclusive = lineTaxes.filter((t) => t.isIncludedInPrice === true);
    const combinedInclusiveRate = inclusive.reduce((a, t) => a + Number(t.rate || 0) / 100, 0);
    const netBeforeInclusive = combinedInclusiveRate > 0 ? lineTotalGross / (1 + combinedInclusiveRate) : lineTotalGross;
    const totalInclusiveTax = r2(lineTotalGross - netBeforeInclusive);
    const netBase = r2(netBeforeInclusive);
    const lastInclusiveIndex = inclusive.length - 1;
    let allocatedInclusiveTax = 0;
    let inclusiveSeen = 0;
    let totalExclusiveTax = 0;
    let lineTaxAmount = 0;
    const taxInfo: TillLineTax[] = [];
    for (const tax of lineTaxes) {
      let calculated: number;
      if (tax.isIncludedInPrice === true) {
        const isLastInclusive = inclusiveSeen === lastInclusiveIndex;
        calculated = isLastInclusive
          ? r2(totalInclusiveTax - allocatedInclusiveTax)
          : r2(combinedInclusiveRate > 0 ? (totalInclusiveTax * (Number(tax.rate) / 100)) / combinedInclusiveRate : 0);
        allocatedInclusiveTax += calculated;
        inclusiveSeen++;
      } else {
        calculated = r2(netBase * (Number(tax.rate) / 100));
        totalExclusiveTax += calculated;
      }
      lineTaxAmount += calculated;
      taxInfo.push({
        taxId: tax.id ?? null,
        rate: Number(tax.rate),
        isIncludedInPrice: tax.isIncludedInPrice === true,
        taxableAmount: netBase,
        calculatedAmount: calculated,
      });
    }
    const totalLineAmount = r2(lineTotalGross + totalExclusiveTax);
    itemsNetOfInclusiveTaxBase += netBase;
    itemsPreDiscountNetBase += combinedInclusiveRate > 0 ? l.subtotalAmount / (1 + combinedInclusiveRate) : l.subtotalAmount;
    const lineTaxIncluded = r2(allocatedInclusiveTax);
    const lineTaxExcluded = r2(totalExclusiveTax);
    itemsInclusiveTax += lineTaxIncluded;
    itemsExclusiveTax += lineTaxExcluded;

    const ids = [...l.appliedPromotionIds];
    for (const id of lineOrderPromotionIds[idx]) if (!zeroOrderPromotionIds.has(id) && !ids.includes(id)) ids.push(id);
    priced.push({
      cartIndex: l.cartIndex,
      cartLineId: l.cartLineId,
      itemId: l.itemId,
      quantity: l.quantity,
      categoryId: l.categoryId,
      grantedByPromotionId: l.grantedByPromotionId,
      variantOptionIds: l.variantOptionIds,
      unitPrice: l.unitPrice,
      baseAmount: r2(l.unitPrice * l.quantity),
      variantAmount: r2(l.variantAmount * l.quantity),
      modifierAmount: r2(l.modifierAmount * l.quantity),
      subtotalAmount: r2(l.subtotalAmount),
      discountAmount: r2(l.discountAmount),
      promotionAmount: r2(l.promotionAmount),
      orderPromotionAmount: r2(proratedOrderPromotion),
      orderDiscountAmount: r2(proratedOrderDiscount),
      lineTotalBeforeTax: netBase,
      taxAmount: r2(lineTaxAmount),
      taxIncluded: lineTaxIncluded,
      taxExcluded: lineTaxExcluded,
      totalAmount: totalLineAmount,
      appliedPromotionIds: [...new Set(ids)],
      taxInfo,
    });
  }

  // ORDER_TOTAL-scoped taxes.
  let orderInclusiveTax = 0;
  let orderExclusiveTax = 0;
  if (orderTotalTaxes.length) {
    const base = r2(itemsNetOfInclusiveTaxBase);
    const incl = orderTotalTaxes.filter((t) => t.isIncludedInPrice === true);
    const inclusiveRate = incl.reduce((a, t) => a + Number(t.rate) / 100, 0);
    const net = r2(inclusiveRate > 0 ? base / (1 + inclusiveRate) : base);
    const totalInclusive = r2(base - net);
    let lastInclusive = -1;
    orderTotalTaxes.forEach((t, k) => {
      if (t.isIncludedInPrice === true) lastInclusive = k;
    });
    orderTotalTaxes.forEach((tax, k) => {
      if (tax.isIncludedInPrice === true) {
        const amount =
          k === lastInclusive
            ? r2(totalInclusive - orderInclusiveTax)
            : r2((totalInclusive * (Number(tax.rate) / 100)) / inclusiveRate);
        orderInclusiveTax += amount;
      } else {
        orderExclusiveTax += r2(net * (Number(tax.rate) / 100));
      }
    });
    orderInclusiveTax = r2(orderInclusiveTax);
    orderExclusiveTax = r2(orderExclusiveTax);
  }
  const goodsNetBase = itemsNetOfInclusiveTaxBase - orderInclusiveTax;
  const orderInclusiveRate = orderTotalTaxes
    .filter((t) => t.isIncludedInPrice === true)
    .reduce((a, t) => a + Number(t.rate) / 100, 0);
  const goodsPreDiscountNetBase = itemsPreDiscountNetBase / (1 + orderInclusiveRate);

  // FEES
  const loyaltyRequested = Number(input.loyaltyRedemption ?? 0) || 0;
  const serviceChargePercent = isDineIn ? Number(input.serviceChargePercent ?? 0) || 0 : 0;
  let finalServiceFee = 0;
  if (serviceChargePercent > 0 && isDineIn) {
    const base = Math.max(0, goodsNetBase - loyaltyRequested);
    finalServiceFee = r2((base * serviceChargePercent) / 100);
  }
  const deliveryFee = isDelivery ? Number(input.deliveryFee ?? 0) || 0 : 0;
  const finalDeliveryFee = isFreeDeliveryGranted ? 0 : deliveryFee;

  let deliveryInclusiveTax = 0;
  let deliveryExclusiveTax = 0;
  if (finalDeliveryFee > 0) {
    for (const tax of (input.deliveryTaxes ?? []).filter(taxInForce)) {
      const rate = Number(tax.rate) / 100;
      const calc = r2(tax.isIncludedInPrice === true ? finalDeliveryFee - finalDeliveryFee / (1 + rate) : finalDeliveryFee * rate);
      if (tax.isIncludedInPrice === true) deliveryInclusiveTax += calc;
      else deliveryExclusiveTax += calc;
    }
  }
  let serviceInclusiveTax = 0;
  let serviceExclusiveTax = 0;
  const serviceFeeApplies = finalServiceFee > 0 || (serviceChargePercent > 0 && isDineIn);
  if (serviceFeeApplies) {
    for (const tax of (input.serviceFeeTaxes ?? []).filter(taxInForce)) {
      const rate = Number(tax.rate) / 100;
      const calc = r2(tax.isIncludedInPrice === true ? finalServiceFee - finalServiceFee / (1 + rate) : finalServiceFee * rate);
      if (tax.isIncludedInPrice === true) serviceInclusiveTax += calc;
      else serviceExclusiveTax += calc;
    }
  }

  const itemsFinalTotal = priced.reduce((s, l) => s + l.totalAmount, 0);
  const itemsTotalPromotion = priced.reduce((s, l) => s + l.promotionAmount, 0);

  // SURCHARGES: matched on the gross after reductions, % base on the goods' VAT-net.
  let appliedSurcharges: AppliedSurcharge[] = [];
  let surchargeAmount = 0;
  let surchargeInclusiveTax = 0;
  let surchargeExclusiveTax = 0;
  if (input.surcharges && input.surcharges.length) {
    appliedSurcharges = computeAppliedSurcharges(
      input.surcharges,
      {
        branchId: input.branchId,
        orderType: String(orderType),
        orderSource: channel,
        localDateTime: now,
        itemsSubtotal: itemsGrossSubtotal,
        discountTotal:
          orderDiscountAmount + itemsTotalLineDiscount + orderPromotionAmount + itemsTotalPromotion + loyaltyRequested,
        postDiscountNetBase: Math.max(0, goodsNetBase - loyaltyRequested),
        preDiscountNetBase: goodsPreDiscountNetBase,
      },
      input.surchargeTaxesById ?? {},
    );
    for (const s of appliedSurcharges) {
      if (s.waived) continue;
      surchargeAmount += s.amount;
      for (const t of s.taxes) {
        if (t.isIncluded) surchargeInclusiveTax += t.calculatedAmount;
        else surchargeExclusiveTax += t.calculatedAmount;
      }
    }
  }

  let finalTotal =
    r2(itemsFinalTotal) + r2(finalServiceFee) + r2(finalDeliveryFee) + r2(surchargeAmount) + r2(surchargeExclusiveTax) + orderExclusiveTax;
  if (finalTotal < 0) finalTotal = 0;
  const svcIn = r2(serviceInclusiveTax);
  const svcEx = r2(serviceExclusiveTax);
  const delIn = r2(deliveryInclusiveTax);
  const delEx = r2(deliveryExclusiveTax);
  finalTotal += svcEx + delEx;

  const totalBeforeLoyalty = r2(finalTotal);
  const loyaltyDiscountAmount = r2(Math.min(Math.max(0, loyaltyRequested), Math.max(0, finalTotal)));
  finalTotal = r2(Math.max(0, finalTotal - loyaltyDiscountAmount));

  // Tax split (every bucket a sum of rounded rows).
  const itemIn = r2(itemsInclusiveTax);
  const itemEx = r2(itemsExclusiveTax);
  const surIn = r2(surchargeInclusiveTax);
  const surEx = r2(surchargeExclusiveTax);
  const ordIn = r2(orderInclusiveTax);
  const ordEx = r2(orderExclusiveTax);
  const item = r2(itemIn + itemEx);
  const service = r2(svcIn + svcEx);
  const delivery = r2(delIn + delEx);
  const sur = r2(surIn + surEx);
  const orderTax = r2(ordIn + ordEx);

  return {
    result: {
      itemsSubtotal: r2(itemsGrossSubtotal),
      promotionAmount: r2(orderPromotionAmount + itemsTotalPromotion),
      discountAmount: r2(orderDiscountAmount + itemsTotalLineDiscount),
      taxAmount: r2(item + service + delivery + sur + orderTax),
      taxIncluded: r2(itemIn + svcIn + delIn + surIn + ordIn),
      taxExclusive: r2(itemEx + svcEx + delEx + surEx + ordEx),
      serviceFeeAmount: r2(finalServiceFee),
      deliveryFeeAmount: r2(finalDeliveryFee),
      surchargeAmount: r2(surchargeAmount),
      loyaltyDiscountAmount,
      totalBeforeLoyalty,
      totalAmount: r2(finalTotal),
      appliedPromotionIds: appliedIds,
      promotionAmounts,
      loyaltySignals: signals,
      freeDelivery: isFreeDeliveryGranted,
      reconcilePromotionIds: reconcile,
      lines: priced,
      freeItemLines: [],
      appliedSurcharges,
    },
    pendingGrants,
    zeroOrderPromotionIds,
  };
}

function emptyResult(): PriceCartResult {
  return {
    itemsSubtotal: 0, promotionAmount: 0, discountAmount: 0, taxAmount: 0, taxIncluded: 0, taxExclusive: 0,
    serviceFeeAmount: 0, deliveryFeeAmount: 0, surchargeAmount: 0, loyaltyDiscountAmount: 0, totalBeforeLoyalty: 0,
    totalAmount: 0, appliedPromotionIds: [], promotionAmounts: [], loyaltySignals: {}, freeDelivery: false,
    reconcilePromotionIds: [], lines: [], freeItemLines: [], appliedSurcharges: [],
  };
}

/**
 * Prices a storefront cart exactly as the POS till would price the same order.
 * See the module doc.
 */
export function priceCart(input: PriceCartInput): PriceCartResult {
  const idx = menuIndex(input.menuItems);
  const base = buildLines(input, idx);
  // OrderPricingRun: an order with no lines is priced at zero (no fees, no loyalty).
  if (base.length === 0) return emptyResult();

  const run = (excluded: Set<string>): CoreResult => {
    let pass = calcCore(input, base.map((l) => ({ ...l, appliedPromotionIds: [] })), excluded, true);
    const grants: TillFreeItemLine[] = [];
    if (pass.pendingGrants.length) {
      const appended: WorkLine[] = [];
      let next = base.length;
      for (const g of pass.pendingGrants) {
        const entry = idx.get(g.itemId);
        if (!entry || (entry.item as any).isAvailable === false) continue;
        let variantAmount = 0;
        const variantIds: string[] = [];
        if (g.variantId != null) {
          const opt = findVariantOption(entry.raw, g.variantId);
          if (!opt) continue;
          variantIds.push(g.variantId);
          variantAmount = Number(opt.price?.amount ?? 0);
        }
        const unitPrice = Number(entry.raw?.price?.amount ?? entry.item.price ?? 0);
        appended.push({
          index: next,
          key: `#${next}`,
          cartIndex: null,
          cartLineId: null,
          itemId: g.itemId,
          // The till's granted line carries no category and no item taxes.
          categoryId: null,
          quantity: g.quantity,
          unitPrice,
          variantAmount,
          modifierAmount: 0,
          subtotalAmount: (unitPrice + variantAmount) * g.quantity,
          discountAmount: 0,
          promotionAmount: 0,
          appliedPromotionIds: [],
          grantedByPromotionId: g.promotionId,
          variantOptionIds: variantIds,
          taxes: [],
        });
        next++;
        grants.push({ promotionId: g.promotionId, itemId: g.itemId, variantId: g.variantId, quantity: g.quantity, unitPrice: unitPrice + variantAmount });
      }
      if (appended.length) {
        pass = calcCore(
          input,
          [...base.map((l) => ({ ...l, appliedPromotionIds: [] })), ...appended],
          excluded,
          false,
        );
      }
    }
    pass.result.freeItemLines = grants;
    return pass;
  };

  let pass = run(new Set());
  if (pass.zeroOrderPromotionIds.size) pass = run(pass.zeroOrderPromotionIds);
  return pass.result;
}
